import React, { useEffect, useMemo, useState, useCallback } from "react"
import LoadingFrame from '../../components/LoadingFrame'
import axios from "axios"
import { ErrorPage } from "../ErrorPage"
import { Worker, Viewer, ScrollMode } from '@react-pdf-viewer/core'
import { scrollModePlugin } from '@react-pdf-viewer/scroll-mode';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { themePlugin } from '@react-pdf-viewer/theme';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

import '@react-pdf-viewer/core/lib/styles/index.css'
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import {
    HeaderText,
    CheckBoxChecked,
    CheckBoxEmpty,
    Wrapper,
    PdfContainer,
    ButtonExternalContainer,
    ButtonInternalContainer,
    ButtonDescription,
    ButtonRefresh,
    Header,
    HeaderLogo,
    HeaderLeftSide,
} from './styles'
import * as S from './styles'
import * as G from '../../theme/global-styles'
import PoweredByQITech from "../../components/PoweredByQITech";
import ClientLogo from "../../components/ClientLogo";
import {
    isMobile
} from '../../utils/utils'
import { ThemedButton } from "../../components/ThemedButton"
import { Wallpaper } from '../../components/Wallpaper';
import { Refresh, Download } from "@mui/icons-material"
import { Button } from '../../components';
import { useThemeContext } from "../../contexts/ThemeContext";
import { getOrCreateBrowserKey } from '../../utils/fingerprint_utils'
import { LoadingPage } from "../LoadingPage"

const renderMinimalToolbar = (Toolbar) => (
    <Toolbar>
        {(slots) => {
            const {
                ZoomOut,
                Zoom,
                ZoomIn,
                GoToPreviousPage,
                GoToNextPage,
                Print,
                Download
            } = slots;

            return (
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        width: '100%',
                        backgroundColor: '#242424',
                        padding: '4px',
                        '--rpv-core__minimal-button-color': '#ffffff',
                        '--rpv-toolbar__label-color': '#ffffff',
                        '--rpv-core__minimal-button-hover-background-color': 'rgba(255, 255, 255, 0.1)',
                    }}
                >
                    <style>{`
                        .rpv-core__minimal-button, 
                        .rpv-core__icon {
                            color: #ffffff !important;
                        }
                        .rpv-core__minimal-button:disabled {
                            color: #555555 !important;
                            cursor: not-allowed;
                        }
                    `}</style>
                    <div style={{ display: 'flex', width: '100%' }}>
                        <div style={{ display: 'flex', flex: '1', justifyContent: 'flex-start' }}>
                            <div style={{ padding: '0 2px' }}><ZoomOut /></div>
                            <div style={{ padding: '0 2px' }}><Zoom /></div>
                            <div style={{ padding: '0 2px' }}><ZoomIn /></div>
                        </div>

                        <div style={{ display: 'flex', flex: '1', justifyContent: 'center' }}>
                            <div style={{ padding: '0 2px' }}><GoToPreviousPage /></div>
                            <div style={{ padding: '0 2px' }}><GoToNextPage /></div>
                        </div>

                        <div style={{ display: 'flex', flex: '1', justifyContent: 'flex-end' }}>
                            <div style={{ padding: '0 2px' }}><Download /></div>
                            <div style={{ padding: '0 2px' }}><Print /></div>
                        </div>
                    </div>
                </div>
            );
        }}
    </Toolbar>
);

export const DocumentsInbox = (props) => {
    const { clientThemeSettings } = useThemeContext();

    const scrollModePluginInstance = scrollModePlugin()
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;
    const themePluginInstance = themePlugin();
    const zoomPluginInstance = zoomPlugin({ enableShortcuts: true });
    const { signData, setSignerStep, setSignData, appTheme } = props
    const [loading, setLoading] = useState(true)
    const initialError = {
        "has_error": false,
        "status": 0
    }
    const [error, setError] = useState(initialError)
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
    const [pdfSrc, setPdfSrc] = useState('')
    const [disablePrimaryButton, setDisabledPrimaryButton] = useState(true)
    const [pdfInstanceKey, setPdfInstanceKey] = useState(0)
    const [wasRead, setWasRead] = useState(signData.documents.map(() => ({
        wasRead: false,
    })))

    const shouldReinforceDocumentsAcceptance = Boolean(signData.reinforce_documents_acceptance);
    const [currentSubStep, setCurrentSubStep] = useState('documents');
    const [reinforcementChecks, setReinforcementChecks] = useState(signData.documents.map(() => false))

    const pdfWorkerUrl = useMemo(() => {
        try {
            // eslint-disable-next-line global-require
            const pdfjsVersion = require('pdfjs-dist/package.json')?.version;
            return `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`;
        } catch {
            return 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
        }
    }, []);

    useEffect(() => {
        const config = {
            headers:
            {
                'Authorization': signData.signer_token
            }
        }

        axios.get('/signer/documents', config)
            .then(response => {
                const docs = response.data.documents || [];
                const reverse_docs = [...response.data.documents].reverse();
                setSignData((prevData) => ({ ...prevData, documents: reverse_docs }));
                setWasRead(docs.map(() => ({ wasRead: false })));
            })
            .catch((error) => {
                if ([401, 403, 409].includes((error.response || {}).status)) {
                    setError({
                        "has_error": true,
                        "status": error.response.status
                    })
                } else {
                    setError({
                        "has_error": true,
                        "status": 500
                    })
                }
            })
            .finally(() => {
                setLoading(false)
            })

    }, [setSignData, signData.signer_token])

    const reloadPage = () => {
        setDisabledPrimaryButton(true)
        setPdfInstanceKey((prev) => prev + 1)
    }

    const downloadPDF = (pdfUrl, documentName) => {
        const link = document.createElement('a')
        link.href = pdfUrl
        link.download = documentName + '.pdf'
        link.target = '_blank'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const onSubmit = async () => {
        let browserKey = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (err) {
        }
        const data = {
            "event_date": new Date().toISOString(),
            "step": "document_display",
            "device_data": {
                "device_key": browserKey
            }
        }
        const config = {
            headers:
            {
                'Authorization': signData.signer_token
            }
        }

        await axios.patch('/signer', data, config)
            .then(response => {
                setSignerStep(response.data.next_step)
                setSignData({ ...signData, ...response.data })
            })
            .catch((error) => {
                if ([401, 403, 409].includes((error.response || {}).status)) {
                    setError({
                        "has_error": true,
                        "status": error.response.status
                    })
                } else {
                    setError({
                        "has_error": true,
                        "status": 500
                    })
                }
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const setPdfLink = useCallback((nextDocumentIndex) => {
        const documentUrl = signData.documents[nextDocumentIndex]?.url
        if (!documentUrl) {
            setPdfSrc('')
            return
        }

        setPdfSrc(documentUrl)
    }, [signData.documents])

    const setReadDocuments = async (documentKey) => {
        const updatedWasRead = wasRead.map((documentReadState, index) =>
            index === documentKey ? { wasRead: true } : documentReadState
        )
        setWasRead(updatedWasRead)

        if (documentKey + 1 === signData.documents.length) {

            if (shouldReinforceDocumentsAcceptance) {
                setCurrentSubStep('reinforcement');
                return;
            }

            await onSubmit()
            return
        }

        setPdfLink(documentKey + 1)
        setCurrentDocumentIndex(documentKey + 1)
        reloadPage()
    }

    const enableSignButton = useCallback(() => setDisabledPrimaryButton(false), [])

    useEffect(() => {
        setPdfLink(currentDocumentIndex)
        setDisabledPrimaryButton(true)
        setPdfInstanceKey((prev) => prev + 1)
    }, [currentDocumentIndex, setPdfLink])

    const renderPdfFallbackIframe = useCallback((iframeStyle) => {
        const iframeSrc = isMobile()
            ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(pdfSrc)}&cachebust=${pdfInstanceKey}`
            : pdfSrc

        return (
            <iframe
                key={`${currentDocumentIndex}-${pdfInstanceKey}-iframe`}
                title={signData.documents[currentDocumentIndex].name}
                src={iframeSrc}
                onLoad={() => {
                    enableSignButton()
                }}
                style={iframeStyle}
            />
        )
    }, [currentDocumentIndex, enableSignButton, pdfInstanceKey, pdfSrc, signData.documents])

    const toggleReinforcementCheckbox = useCallback((documentIndex) => {
        setReinforcementChecks((prevChecks) =>
            prevChecks.map((checked, index) => (index === documentIndex ? !checked : checked))
        );
    }, []);

    const allReinforcementChecked = useMemo(() => {
        return reinforcementChecks.length > 0 && reinforcementChecks.every(Boolean);
    }, [reinforcementChecks]);


    const renderReinforcementSubStep = useCallback(() => {
        const documents = signData.documents || [];

        return (
            <S.ReinforcementContainer>
                <G.Title>Confirmação de ciência</G.Title>
                <S.Description style={{ textAlign: 'unset', padding: '20px 0' }}>
                    Precisamos da sua confirmação sobre a contratação de múltiplos produtos na mesma jornada de assinatura.
                </S.Description>
                <S.Description style={{ textAlign: 'unset', paddingBottom: '20px' }}>
                    É necessário marcar todos os produtos contratados para avançar para a próxima etapa:
                </S.Description>
                <S.ReinforcementCheckboxesContainer>
                    {documents.map((document, index) => {
                        const checkboxId = `reinforcement-doc-${index}`;
                        return (
                            <S.CheckboxContainer key={document?.key || document?.name || index}>
                                <input
                                    style={{ transform: 'scale(1.5)' }}
                                    id={checkboxId}
                                    type="checkbox"
                                    checked={reinforcementChecks[index] || false}
                                    onChange={() => toggleReinforcementCheckbox(index)}
                                />
                                <S.CheckboxLabel htmlFor={checkboxId}>
                                    <strong>{document?.name}</strong>
                                </S.CheckboxLabel>
                            </S.CheckboxContainer>
                        );
                    })}
                </S.ReinforcementCheckboxesContainer>

            </S.ReinforcementContainer>
        );
    }, [reinforcementChecks, signData.documents, toggleReinforcementCheckbox]);

    const renderPdf = useCallback(() => {
        if (!pdfSrc) {
            return (
                <S.SkeletonContainer>
                    <LoadingFrame />
                </S.SkeletonContainer>
            )
        }

        const iframeStyle = { width: '100%', height: '100%', border: 'none' }

        return (
            <div style={{
                height: isMobile() ? '60vh' : '70vh',
                width: '100%',
                border: '1px solid #333',
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {renderMinimalToolbar(Toolbar)}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <Worker workerUrl={pdfWorkerUrl}>
                        <Viewer
                            theme='dark'
                            key={`${currentDocumentIndex}-${pdfInstanceKey}-viewer`}
                            fileUrl={pdfSrc}
                            defaultScale={isMobile() ? 0.6 : 1.5}
                            plugins={[scrollModePluginInstance, toolbarPluginInstance, themePluginInstance, zoomPluginInstance]}
                            initialScrollMode={ScrollMode.Vertical}
                            onDocumentLoad={() => {
                                if (!shouldReinforceDocumentsAcceptance) {
                                    enableSignButton();
                                    return;
                                }
                            }}
                            onPageChange={(e) => {
                                if (!shouldReinforceDocumentsAcceptance) return;

                                const isLastPage = e.currentPage + 1 >= e.doc.numPages;
                                if (isLastPage) {
                                    enableSignButton();
                                }
                            }}
                            renderLoader={() => (
                                <S.SkeletonContainer>
                                    <LoadingFrame />
                                </S.SkeletonContainer>
                            )}
                            renderError={() => renderPdfFallbackIframe(iframeStyle)}
                        />
                    </Worker>
                </div>
            </div>
        )
    }, [
        currentDocumentIndex,
        enableSignButton,
        pdfInstanceKey,
        pdfSrc,
        pdfWorkerUrl,
        renderPdfFallbackIframe,
        scrollModePluginInstance,
        toolbarPluginInstance,
        themePluginInstance,
        zoomPluginInstance,
        Toolbar,
        shouldReinforceDocumentsAcceptance
    ])


    const handlePrimaryAction = async () => {
        if (currentSubStep === 'reinforcement') {
            if (!allReinforcementChecked) return;
            await onSubmit();
            return;
        }

        await setReadDocuments(currentDocumentIndex);
    };


    const isPrimaryButtonDisabled = currentSubStep === 'reinforcement'
        ? !allReinforcementChecked || loading
        : disablePrimaryButton || loading;

    if (loading) {
        return (
            <LoadingPage />
        )
    }

    if (appTheme === 'release') {
        return (
            <S.Container>
                <S.Header>
                    <S.HeaderSide>
                        <ClientLogo clientThemeSettings={clientThemeSettings} size='small' />
                    </S.HeaderSide>
                    {currentSubStep === 'documents' && <S.HeaderSide>
                        <HeaderText>
                            Documentos:
                        </HeaderText>
                        {wasRead.map((documentReadState, index) => (
                            !documentReadState.wasRead ?
                                <CheckBoxEmpty key={index} $color={'#d7d7d7'} /> :
                                <CheckBoxChecked key={index} $color={clientThemeSettings.background_color} />
                        ))}
                    </S.HeaderSide>}
                </S.Header>
                <S.DocumentContainer>
                    <G.Description style={{ margin: '10px' }}>
                        {currentSubStep === 'documents' && signData.documents[currentDocumentIndex].name}
                    </G.Description>

                    {currentSubStep === 'reinforcement' ? renderReinforcementSubStep() : renderPdf()}
                </S.DocumentContainer>
                <S.Footer>
                    {
                        shouldReinforceDocumentsAcceptance && currentSubStep === 'documents' && (
                            <S.Description>O botão será habilitado ao ler a <strong>última página</strong>.</S.Description>
                        )
                    }
                    <S.ControlBar>
                        <S.SignButtonWrapper>
                            <ThemedButton
                                onClick={handlePrimaryAction}
                                clientThemeSettings={clientThemeSettings}
                                loading={loading}
                                disabled={isPrimaryButtonDisabled}
                                style={currentSubStep === 'reinforcement' ? { maxWidth: '700px', margin: '10px auto' } : { margin: '10px auto' }}
                            >
                                Continuar
                            </ThemedButton>
                        </S.SignButtonWrapper>
                    </S.ControlBar>
                    <PoweredByQITech />
                </S.Footer>
                {error.has_error &&
                    <ErrorPage
                        error={error}
                        redirectToLogin={() => setSignerStep("login")}
                        resetError={() => setError(initialError)}
                        clientThemeSettings={clientThemeSettings}
                    />
                }
            </S.Container>
        );
    }

    return (
        <Wrapper>
            <PdfContainer>
                <Header>
                    <HeaderLogo
                        src={clientThemeSettings.logo_url}
                        alt="Logo Cliente"
                    />
                    <HeaderLeftSide>
                        <HeaderText>
                            Documentos:
                        </HeaderText>
                        {wasRead.map((documentReadState, index) => (
                            !documentReadState.wasRead ?
                                <CheckBoxEmpty key={index} $color={'#d7d7d7'} /> :
                                <CheckBoxChecked key={index} $color={clientThemeSettings.background_color} />
                        ))}
                    </HeaderLeftSide>
                </Header>
                {currentSubStep === 'reinforcement' ? renderReinforcementSubStep() : renderPdf()}
                <ButtonExternalContainer>
                    <ButtonDescription>
                        {signData.documents[currentDocumentIndex].name}
                    </ButtonDescription>
                    <ButtonInternalContainer>
                        <ButtonRefresh
                            onClick={reloadPage}
                            clientThemeSettings={clientThemeSettings}
                            type={'primaryGhost'}
                            disabled={loading}
                        >
                            <Refresh />
                        </ButtonRefresh>

                        <ButtonRefresh
                            onClick={() => (downloadPDF(signData.documents[currentDocumentIndex].url, signData.documents[currentDocumentIndex].name))}
                            clientThemeSettings={clientThemeSettings}
                            type={'primaryGhost'}
                        >
                            <Download />
                        </ButtonRefresh>

                        <Button
                            onClick={() => {
                                setReadDocuments(currentDocumentIndex)
                            }}
                            clientThemeSettings={clientThemeSettings}
                            loading={loading}
                            disabled={disablePrimaryButton}
                        >
                            {currentSubStep === 'reinforcement' ? 'CONTINUAR' : 'ASSINAR'}
                        </Button>
                    </ButtonInternalContainer>
                </ButtonExternalContainer>
            </PdfContainer>
            {error.has_error && (
                <Wallpaper
                    backgroundColor={clientThemeSettings["background_color"]}
                >
                    {error.has_error &&
                        <ErrorPage
                            error={error}
                            redirectToLogin={() => setSignerStep("login")}
                            resetError={() => setError(initialError)}
                            clientThemeSettings={clientThemeSettings}
                        />
                    }
                </Wallpaper>
            )}
        </Wrapper>

    )

}